/* eslint react-hooks/exhaustive-deps: "warn" */
import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './InsuranceComparison.module.scss';

type Translate = (key: string) => string;

const getRows = (t: Translate) => [
  {
    label: t('Source of record'),
    gdb: t('Government registries, collected directly'),
    bureau: t('Blended vendor files, trade data and modelled inputs'),
    broker: t('Self-declared by the party seeking cover'),
  },
  {
    label: t('Attribution'),
    gdb: t('Registry, filing reference and retrieval time on every field'),
    bureau: t('Usually one citation for the whole file'),
    broker: t('None beyond the form'),
  },
  {
    label: t('Group structure'),
    gdb: t('Read from share registers and beneficial-ownership filings'),
    bureau: t('Often inferred from a proprietary linkage graph'),
    broker: t('Declared only if the broker knows to ask'),
  },
  {
    label: t('Ownership tests'),
    gdb: t('Resolved holdings, so aggregate thresholds can be applied'),
    bureau: t("Depends on the vendor's own linkage coverage"),
    broker: t('Out of scope'),
  },
  {
    label: t('Identifiers'),
    gdb: t('Open registry numbers and VAT, joinable to your own data'),
    bureau: t('Proprietary keys that anchor you to the vendor'),
    broker: t('Free text'),
  },
  {
    label: t('Change of position'),
    gdb: t('Daily-synced feeds carrying the filing that moved'),
    bureau: t('Refresh cycles set by the vendor'),
    broker: t('At next renewal'),
  },
  {
    label: t('Use in products you sell'),
    gdb: t('Redistribution and AI training rights available'),
    bureau: t('Restricted by default'),
    broker: t('Not applicable'),
  },
];

export const InsuranceComparison = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.1 });

  const rows = getRows(t);

  return (
    <Section className={styles.root}>
      <div className={styles.head}>
        <TitleBlock
          title={t('Against a bureau file, and against the submission itself.')}
          description={t(
            'Three sources sit behind a commercial risk. Only one of them is written under statutory obligation.',
          )}
        />
      </div>

      <div className={cn(styles.card, revealed && styles.cardIn)} ref={ref}>
        <div className={styles.tableHold}>
          <table className={styles.ct}>
            <thead>
              <tr>
                <th />
                <th className={styles.hi}>
                  <span className={styles.brand}>
                    <i className={styles.brandDot} /> Global Database
                  </span>
                </th>
                <th>
                  {t('Bureau files')} · D&amp;B, Moody{"'"}s
                </th>
                <th>{t('The broker submission')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  className={cn(styles.row, revealed && styles.rowIn)}
                  style={{ transitionDelay: `${i * 45}ms` }}
                  key={row.label}
                >
                  <th>{row.label}</th>
                  <td className={styles.hi}>
                    <span className={styles.cc}>
                      <span className={styles.mkY}>✓</span>
                      <span>{row.gdb}</span>
                    </span>
                  </td>
                  <td>
                    <span className={styles.cc}>
                      <span className={styles.mkN} />
                      <span>{row.bureau}</span>
                    </span>
                  </td>
                  <td>
                    <span className={styles.cc}>
                      <span className={styles.mkN} />
                      <span>{row.broker}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <p className={styles.note}>
        {t('Comparison reflects the standard commercial position of each source. Vendor terms vary by contract.')}
      </p>
    </Section>
  );
};
